import { useNavigate, useParams } from 'react-router-dom';
import CategoryDetailPage from './CategoryDetailPage';

import { useRoadmapQuery, useRoadmapRequestArrangeCallbackMutation } from '@hooks/roadmaps/index';
import { 
  convertToRiskProfile, 
  convertToAccountOwner, 
  Filter,
  FilterData,
  IOption
} from '@cloudclevr/components';
import { RecommendationDto } from '@hooks/roadmaps/types';
import { useCallback, useState, useRef, useMemo,useEffect } from 'react';
import { PageHeader } from '@components/partials/PageHeader';
//import { DownloadAttachmentsModal } from '@cloudclevr/components';
import { DownloadAttachmentsModal } from './DownloadAttachmentsModal';
import useUIStore from '@store/uiStore';
import { Variant } from '@components/ui/Toast';
import { useLocation } from 'react-router-dom';
import { useCategoryRecommendationsQuery } from '@hooks/roadmaps/useRecommendationMutations';
import { useDropdownOptionsQuery } from '@hooks/roadmaps/useDropdownOptionsQuery';
import AccountOwnerCard from './AccountOwnerCard';
import RoadmapHeader from './RoadmapHeader';
import { ViewNotesModal } from '@cloudclevr/components';
import { hasCustomerVisibleCosts } from '@hooks/roadmaps/types';


const CategoryDetailPageWrapper = () => {
  const { categoryId, roadmapId } = useParams<{ categoryId: string; roadmapId: string }>();
  const location = useLocation();

  const navigate = useNavigate();
  const { addToast } = useUIStore();

 
// Update the useEffect to watch for location changes (including query params)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Also reset body scroll in case modal left it in a weird state
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, [categoryId, roadmapId, location.search]);
  

  
  // Modal states
  const [isAcceptRecommendationModalOpen, setIsAcceptRecommendationModalOpen] = useState(false);
  const [isReopenRecommendationModalOpen, setIsReopenRecommendationModalOpen] = useState(false);
  const [isDeclineRecommendationModalOpen, setIsDeclineRecommendationModalOpen] = useState(false);
  const [isInProgressRecommendationModalOpen, setIsInProgressRecommendationModalOpen] = useState(false);
  const [isCompleteRecommendationModalOpen, setIsCompleteRecommendationModalOpen] = useState(false); 
  const [isDeleteRecommendationModalOpen, setIsDeleteRecommendationModalOpen] = useState(false); 
  const [isEditCostsModalOpen, setIsEditCostsModalOpen] = useState(false);
  const [isViewNotesModalOpen, setIsViewNotesModalOpen] = useState(false);
  const [isDownloadAttachmentsModalOpen, setIsDownloadAttachmentsModalOpen] = useState(false);

  // Practice filter state
  const [practiceFilter, setPracticeFilter] = useState<number[]>([]);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // Fetch practice dropdown options for filter drawer
  const { data: practiceOptions = [] } = useDropdownOptionsQuery('PRACTICE-DROPDOWN');

  // Build filter data for the library Filter component
  const practiceFilterData: FilterData[] = useMemo(() => [{
    label: 'Practice',
    name: 'practice',
    options: practiceOptions.map(opt => ({ label: opt.caption, value: opt.value })) as IOption[],
  }], [practiceOptions]);

  // Build practice labels map from BE data for tile pills
  const practiceLabelsMap: Record<number, string> = useMemo(() => {
    const map: Record<number, string> = {};
    practiceOptions.forEach(opt => {
      map[Number.parseInt(opt.value, 10)] = opt.caption;
    });
    return map;
  }, [practiceOptions]);

  // Selected recommendation state
  const [selectedRecommendationId, setSelectedRecommendationId] = useState<string>('');
  const [selectedRecommendationData, setSelectedRecommendationData] = useState<RecommendationDto | undefined>(undefined);

  // Tab switcher functions
  const [switchToAcceptedTab, setSwitchToAcceptedTab] = useState<(() => void) | null>(null);
  const [switchToOpenTab, setSwitchToOpenTab] = useState<(() => void) | null>(null);
  const [switchToDeclinedTab, setSwitchToDeclinedTab] = useState<(() => void) | null>(null);
  const [switchToInProgressTab, setSwitchToInProgressTab] = useState<(() => void) | null>(null);
  const [switchToCompletedTab, setSwitchToCompletedTab] = useState<(() => void) | null>(null);

  // Use refs to prevent state clearing during modal transitions
  const recommendationDataRef = useRef<{
    id: string;
    data: RecommendationDto | undefined;
  }>({ id: '', data: undefined });

  // Fetch roadmap data to get risk profile and account owner info
  const { data: roadmapData, isLoading: isLoadingRoadmap } = useRoadmapQuery(roadmapId);

  // Prepare breadcrumb data
  const breadcrumbData = {
    customerName: roadmapData?.customerName,
    roadmapId: roadmapId
  };

  const handleSetTabSwitcher = useCallback((tabSwitcher: () => void) => {
    setSwitchToAcceptedTab(() => tabSwitcher);
  }, []);

  const handleSetOpenTabSwitcher = useCallback((tabSwitcher: () => void) => {
    setSwitchToOpenTab(() => tabSwitcher);
  }, []);

  const handleSetDeclinedTabSwitcher = useCallback((tabSwitcher: () => void) => {
    setSwitchToDeclinedTab(() => tabSwitcher);
  }, []);

  const handleSetInProgressTabSwitcher = useCallback((tabSwitcher: () => void) => {
    setSwitchToInProgressTab(() => tabSwitcher);
  }, []);

  const handleSetCompletedTabSwitcher = useCallback((tabSwitcher: () => void) => {
    setSwitchToCompletedTab(() => tabSwitcher);
  }, []);

  // Fetch all recommendations for this category to use in edit modal
  const { data: allRecommendations = [] } = useCategoryRecommendationsQuery(
    categoryId,
    undefined,
    ['SubCategory','Attachments']
  );

  // Get the selected recommendation for the modal - with better error handling
  const selectedRecommendation = selectedRecommendationData || allRecommendations.find(rec => String(rec.id) === String(selectedRecommendationId));
  // Improved state setters that use refs to prevent flicker
  const setSelectedRecommendation = useCallback((recommendationId: string) => {
    const recommendation = allRecommendations.find(rec => String(rec.id) === String(recommendationId));
    
    // Store in ref for immediate access
    recommendationDataRef.current = {
      id: recommendationId,
      data: recommendation
    };
    
    // Update state
    setSelectedRecommendationId(recommendationId);
    setSelectedRecommendationData(recommendation);
  }, [allRecommendations]);

  // Improved clear function - remove delay for critical operations
  const clearSelectedRecommendation = useCallback(() => {
    recommendationDataRef.current = { id: '', data: undefined };
    setSelectedRecommendationId('');
    setSelectedRecommendationData(undefined);
  }, []);    
  

  // Improved modal handlers - only clear on actual close, not during success operations
  const handleAcceptRecommendationSuccess = () => {
    setIsAcceptRecommendationModalOpen(false);
    clearSelectedRecommendation();
  };

  const handleAcceptRecommendationClose = () => {
    setIsAcceptRecommendationModalOpen(false);
    clearSelectedRecommendation();
  };

  const handleReopenRecommendationSuccess = () => {
    setIsReopenRecommendationModalOpen(false);
    clearSelectedRecommendation();
  };

  const handleDeclineRecommendationSuccess = () => {
    setIsDeclineRecommendationModalOpen(false);
    clearSelectedRecommendation();
  };

  const handleInProgressRecommendationSuccess = () => {
    setIsInProgressRecommendationModalOpen(false);
    clearSelectedRecommendation();
  };

  const handleReopenRecommendationClose = () => {
    setIsReopenRecommendationModalOpen(false);
    clearSelectedRecommendation();
  };

  const handleDeclineRecommendationClose = () => {
    setIsDeclineRecommendationModalOpen(false);
    clearSelectedRecommendation();
  };

  const handleInProgressRecommendationClose = () => {
    setIsInProgressRecommendationModalOpen(false);
    clearSelectedRecommendation();
  };

  const handleCompleteRecommendationSuccess = () => {
    setIsCompleteRecommendationModalOpen(false);
    clearSelectedRecommendation();
  };

  const handleCompleteRecommendationClose = () => {
    setIsCompleteRecommendationModalOpen(false);
    clearSelectedRecommendation();
  };
  
  const handleDeleteRecommendationSuccess = () => {
    setIsDeleteRecommendationModalOpen(false);
    clearSelectedRecommendation();
  };

  const handleDeleteRecommendationClose = () => {
    setIsDeleteRecommendationModalOpen(false);
    clearSelectedRecommendation();
  };

  // Handle switching from delete modal to decline modal with improved timing
  const handleSwitchToDeclineModal = () => {
    // Store the current recommendation data BEFORE closing the delete modal
    const currentRecommendationData = selectedRecommendationData || recommendationDataRef.current.data;
    const currentRecommendationId = selectedRecommendationId || recommendationDataRef.current.id;
    
    // Close delete modal
    setIsDeleteRecommendationModalOpen(false);
    
    // Use setTimeout to ensure the delete modal is fully closed before opening decline modal
    setTimeout(() => {
      // Restore the recommendation data - DON'T clear it!
      setSelectedRecommendationData(currentRecommendationData);
      setSelectedRecommendationId(currentRecommendationId);
      
      // Update the ref as well to maintain consistency
      recommendationDataRef.current = {
        id: currentRecommendationId,
        data: currentRecommendationData
      };
      
      // Open decline modal
      setIsDeclineRecommendationModalOpen(true);
    }, 50);
  };

  const handleEditCostsClose = () => {
    setIsEditCostsModalOpen(false);
    clearSelectedRecommendation();
  };

  const handleViewNotes = (recommendationId: string) => {
    setSelectedRecommendation(recommendationId);
    setIsViewNotesModalOpen(true);
  };

  const handleViewNotesClose = () => {
    setIsViewNotesModalOpen(false);
    // Delay clearing until modal close animation completes
    setTimeout(() => {
      clearSelectedRecommendation();
    }, 300);
  };

  const handleDownloadAttachments = (recommendationId: string) => {    
    setSelectedRecommendation(recommendationId);
    setIsDownloadAttachmentsModalOpen(true);
  };

  const handleDownloadAttachment = async (roadmapId: string, recommendationId: string, attachmentId: string): Promise<string> => {    
    // TODO: Implement actual download logic and return the file name or URL as a string, for component comming from UI Library
    return Promise.resolve('FileName.pdf');
  };

  const handleDownloadAttachmentsClose = () => {
    setIsDownloadAttachmentsModalOpen(false);
    clearSelectedRecommendation();
  };

  // NEW: Toast notification handlers
   const handleDownloadSuccess = (fileName: string) => {
    addToast({
      variant: Variant.Success,
      message: `File: ${fileName} has downloaded.`,
      closeable: true,
    });
  };

  
  const arrangeCallbackMutation = useRoadmapRequestArrangeCallbackMutation();

  
  const handleArrangeCallback = async (notes?: string) => {
    try {
      await arrangeCallbackMutation.mutateAsync(notes);
    } catch (error) {
      console.error('Error arranging callback:', error);
    }
  };


  const handleDownloadError = (fileName: string) => {
    addToast({
      variant: Variant.Error,
      message: `File: ${fileName} failed to download, please try again.`,
      closeable: true,
    });
  };

  const handleViewQuote = (recommendationId: string) => {
    console.log('View quote clicked for recommendation:', recommendationId);
  };

  // Default values
  const defaultRiskProfile = { level: 'Critical' as const };
  const defaultAccountOwner = {
    name: "Unknown",
    email: "unknown@example.com",
    avatar: undefined
  };

  // Get data from roadmap or use defaults  
  const accountOwner = roadmapData ? convertToAccountOwner(roadmapData) : defaultAccountOwner;

  const handleBackClick = () => {
    if (roadmapId) {
      navigate(`/roadmaps/${roadmapId}`);
    } else {
      navigate('/roadmaps');
    }
  };

  // Show loading state while fetching roadmap data
  if (isLoadingRoadmap) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }  

  return (
    <>
      <div className="px-3 py-0">
        {/* <PageHeader breadcrumbData={breadcrumbData} /> */}
        <PageHeader customTitle={roadmapData?.categories.find(cat => cat.id === categoryId)?.name}  />
      </div>
       <div className="px-6 py-0 pt-0 mt-0 ">
        {/* Roadmap Header */}
        <RoadmapHeader  data-testid="roadmap-header"
          customerName={roadmapData?.customerName || 'Customer'} 
          roadmapId={roadmapData?.id || ''}      
          riskProfile={roadmapData?.categories.find(cat => cat.id === categoryId)?.severity ? convertToRiskProfile(roadmapData.categories.find(cat => cat.id === categoryId)!.severity) : defaultRiskProfile}          
          showCostBreakdown={ !!roadmapData && hasCustomerVisibleCosts(roadmapData.recommendations || [])}
          preselectedCategoryId={categoryId}
          //onViewAllRecommendations={handleViewAllRecommendations}          
        />
      </div>
      
      
      {/* Main content wrapper with flex layout */}
      <div data-testid="category-detail-wrapper" className="flex gap-3 px-3">
        {/* Left side - Category Detail Page */}
        <div data-testid="category-detail-section" className="flex-1">
          <CategoryDetailPage
            categoryId={categoryId || ''}
            categoryName={roadmapData?.categories.find(cat => cat.id === categoryId)?.name || 'My Category'}
            customerName={roadmapData?.customerName}
            roadmapId={roadmapId || ''}
            riskProfile={roadmapData?.categories.find(cat => cat.id === categoryId)?.severity ? convertToRiskProfile(roadmapData.categories.find(cat => cat.id === categoryId)!.severity) : defaultRiskProfile}
            accountOwner={accountOwner}
            onBackClick={handleBackClick}
            onViewNotes={handleViewNotes}
            onDownloadAttachments={handleDownloadAttachments}   
            onDownloadAttachment={handleDownloadAttachment}      
            onViewQuote={handleViewQuote}        
            onSetTabSwitcher={handleSetTabSwitcher}
            onSetOpenTabSwitcher={handleSetOpenTabSwitcher}
            onSetDeclinedTabSwitcher={handleSetDeclinedTabSwitcher}
            onSetInProgressTabSwitcher={handleSetInProgressTabSwitcher}  
            onSetCompletedTabSwitcher={handleSetCompletedTabSwitcher} 
            onDownloadSuccess={handleDownloadSuccess}
            onDownloadError={handleDownloadError}
            practiceFilter={practiceFilter.length > 0 ? practiceFilter : undefined}
            practiceLabels={practiceLabelsMap}
            onFilterClick={() => setIsFilterDrawerOpen(true)}
            activeFilterCount={practiceFilter.length}
            showFilterButton={allRecommendations.some(r => r.practice != null) || practiceFilter.length > 0}
          />
        </div>

        {/* Right side - Account Owner Card */}
        <div data-testid="account-owner-section" className="w-80 flex-shrink-0 mt-[10px]">
          <div className="sticky top-2">
            <AccountOwnerCard
              accountOwnerName={roadmapData?.accountOwnerName || 'Unknown'}
              accountOwnerEmail={roadmapData?.accountOwnerEmailAddress}
              accountOwnerImageUrl={undefined}
              onArrangeCallback={handleArrangeCallback}
              isVerticalLayout={true} 
            />
          </div>
        </div>
      </div>     

      {/* Practice Filter Drawer */}
      <Filter
        isDrawerOpen={isFilterDrawerOpen}
        onCloseDrawer={() => setIsFilterDrawerOpen(false)}
        filterData={practiceFilterData}
        queryParams={{ filter: practiceFilter.map(String).join(',') || undefined }}
        setQueryParams={(params) => {
          const filterStr = params.filter || '';
          const values = filterStr ? filterStr.split(',').map(Number).filter(n => !Number.isNaN(n)) : [];
          setPracticeFilter(values);
        }}
        setPageNumber={() => {}}
        refetchQuery={() => Promise.resolve({} as any)}
        headerText="Filter by Practice"
      />

      {/* View Notes Modal */}
      <ViewNotesModal
        recommendation={selectedRecommendation}
        isModalOpen={isViewNotesModalOpen}
        handleModalClose={handleViewNotesClose}
      />

      {/* Download Attachments Modal */}
      <DownloadAttachmentsModal
        recommendation={selectedRecommendation}
        roadmapId={roadmapId}
        isModalOpen={isDownloadAttachmentsModalOpen}
        handleModalClose={handleDownloadAttachmentsClose}
        onDownloadSuccess={handleDownloadSuccess}
        onDownloadError={handleDownloadError}      
      />
    </>
  );
};

export default CategoryDetailPageWrapper;